import { useState, useRef, useMemo, useEffect, type ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { DeliveryPanel } from '../components/delivery/DeliveryPanel';

interface PreviewRow {
  [key: string]: string | number | boolean | null;
}

interface RejectedRecord {
  directus_id: string;
  l_name: string;
  f_name: string;
  company: string;
  rejected_at: string;
}

interface SpamFlag {
  directus_id: string;
  l_name: string;
  f_name: string;
  company: string;
  message: string;
}

const FIELD_LABELS: Record<string, string> = {
  l_name: 'Фамилия',
  f_name: 'Имя',
  p_name: 'Отчество',
  company: 'Компания',
  role: 'Должность',
  country: 'Страна',
  email: 'Email',
  directus_id: 'ID',
  pay_date: 'Оплата',
  registered: 'Регистрация',
  dinner: 'Ужин',
  root: 'Строка',
};

type ImportSource = 'file' | 'api';

const PREVIEW_PAGE_SIZE = 100;

export function ImportPage() {
  const [source, setSource] = useState<ImportSource>('file');
  const [mode, setMode] = useState<'append' | 'replace'>('append');
  const [preview, setPreview] = useState<PreviewRow[] | null>(null);
  const [totalReceived, setTotalReceived] = useState(0);
  const [previewPage, setPreviewPage] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ inserted: number; skipped: number } | null>(null);
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [spamList, setSpamList] = useState<SpamFlag[]>([]);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showSpamDetails, setShowSpamDetails] = useState(false);
  const [removedRows, setRemovedRows] = useState<PreviewRow[]>([]);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [existingIds, setExistingIds] = useState<Set<string>>(new Set());
  const [rejectedList, setRejectedList] = useState<RejectedRecord[]>([]);
  const [rejectedLoaded, setRejectedLoaded] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (source === 'api' && !rejectedLoaded) {
      api.guests.listRejected()
        .then((res) => {
          setRejectedList(res.rejected || []);
          setRejectedLoaded(true);
        })
        .catch(() => {});
    }
  }, [source, rejectedLoaded]);

  const spamMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of spamList) {
      if (s.directus_id) map.set(String(s.directus_id), s.message);
    }
    return map;
  }, [spamList]);

  const spamInPreview = useMemo(() => {
    if (!preview || spamMap.size === 0) return 0;
    let n = 0;
    for (const row of preview) {
      const id = row.directus_id ? String(row.directus_id) : '';
      if (id && spamMap.has(id)) n++;
    }
    return n;
  }, [preview, spamMap]);

  const removeRow = (index: number) => {
    if (preview && source === 'api' && preview[index]?.directus_id) {
      setRemovedRows(prev => [...prev, preview[index]]);
    }
    setPreview(prev => prev ? prev.filter((_, i) => i !== index) : null);
  };

  async function handleRestore(directus_id: string) {
    try {
      await api.guests.restoreRejected(directus_id);
      setRejectedList(prev => prev.filter(r => r.directus_id !== directus_id));
    } catch (err: any) {
      setError(err.message || 'Не удалось восстановить запись');
    }
  }

  async function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setResult(null);
    setPreview(null);
    setTotalReceived(0);
    setPreviewPage(1);
    setImportErrors([]);
    setSpamList([]);
    setShowErrorDetails(false);
    setShowSpamDetails(false);
    setRemovedRows([]);
    setRejectedCount(0);
    setExistingIds(new Set());
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      setSource('file');
      const res = await api.guests.importPreview(formData);
      setPreview(res.rows || []);
      setTotalReceived(res.totalReceived ?? (res.rows || []).length);
      setImportErrors(res.errors || []);
      setSpamList(res.spam || []);
      setRejectedCount(res.rejectedCount || 0);
      setExistingIds(new Set(res.existingIds || []));
    } catch {
      setError('Не удалось обработать файл');
    } finally {
      setLoading(false);
    }
  }

  async function handleFetchApi() {
    setError('');
    setResult(null);
    setPreview(null);
    setTotalReceived(0);
    setPreviewPage(1);
    setImportErrors([]);
    setSpamList([]);
    setShowErrorDetails(false);
    setShowSpamDetails(false);
    setRemovedRows([]);
    setRejectedCount(0);
    setExistingIds(new Set());
    setLoading(true);

    try {
      setSource('api');
      const res = await api.guests.fetchApi();
      setPreview(res.rows || []);
      setTotalReceived(res.totalReceived ?? (res.rows || []).length);
      setImportErrors(res.errors || []);
      setSpamList(res.spam || []);
      setRejectedCount(res.rejectedCount || 0);
      setExistingIds(new Set(res.existingIds || []));
    } catch (err: any) {
      setError(err.message || 'Не удалось загрузить данные из API');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    if (!preview || preview.length === 0) return;
    setLoading(true);
    setError('');

    try {
      const rejected = removedRows.map((r) => ({
        directus_id: String(r.directus_id ?? ''),
        l_name: String(r.l_name ?? ''),
        f_name: String(r.f_name ?? ''),
        company: String(r.company ?? ''),
      }));
      const res = await api.guests.importConfirm(preview, mode, rejected);
      setResult({ inserted: res.inserted, skipped: res.skipped });
      setPreview(null);
      setRemovedRows([]);
      setRejectedLoaded(false);
      queryClient.invalidateQueries({ queryKey: ['delivery-log'] });
      queryClient.invalidateQueries({ queryKey: ['delivery-summary'] });
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setPreview(null);
    setTotalReceived(0);
    setPreviewPage(1);
    setResult(null);
    setError('');
    setImportErrors([]);
    setSpamList([]);
    setShowErrorDetails(false);
    setShowSpamDetails(false);
    setRemovedRows([]);
    setRejectedCount(0);
    setExistingIds(new Set());
    if (fileRef.current) fileRef.current.value = '';
  }

  function switchSource(s: ImportSource) {
    setSource(s);
    handleReset();
  }

  const btnPrimary = 'rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-6 py-2 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98] disabled:opacity-50';

  const btnSecondary = 'rounded-lg border border-ct-border bg-ct-surface px-5 py-2 text-sm font-semibold text-ct-text-muted shadow-ct-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md hover:text-ct-primary active:scale-[0.98]';

  const errorRowCount = useMemo(() => {
    const set = new Set<number>();
    for (const err of importErrors) {
      if (typeof err.row === 'number') set.add(err.row);
    }
    return set.size;
  }, [importErrors]);

  const previewTotal = preview?.length ?? 0;
  const previewTotalPages = Math.max(1, Math.ceil(previewTotal / PREVIEW_PAGE_SIZE));
  const safePreviewPage = Math.min(previewPage, previewTotalPages);
  const pageStart = (safePreviewPage - 1) * PREVIEW_PAGE_SIZE;
  const pageRows = preview ? preview.slice(pageStart, pageStart + PREVIEW_PAGE_SIZE) : [];
  const existingInPreview = mode === 'append' && preview
    ? preview.filter((r) => r.directus_id && existingIds.has(String(r.directus_id))).length
    : 0;
  const newCount = Math.max(0, previewTotal - existingInPreview);

  return (
    <div>
      <div className="relative">
        <div className="pointer-events-none fixed inset-0 overflow-hidden -z-10" aria-hidden="true">
          <div className="absolute top-[15%] -right-[10%] h-[350px] w-[350px] rounded-full bg-gradient-to-br from-ct-secondary/10 to-ct-primary/10 blur-[100px]" />
        </div>
      </div>
      <div className="mb-4 inline-flex items-center gap-3 rounded-full bg-indigo-50 border border-indigo-100 px-5 py-2">
        <span className="h-2 w-2 rounded-full bg-ct-primary" />
        <span className="text-xs font-bold uppercase tracking-wider text-ct-primary">Данные</span>
      </div>
      <h1 className="mb-6 text-4xl font-extrabold tracking-tight text-ct-text">
        Импорт списка участников{' '}
        <span className="bg-gradient-to-r from-ct-primary to-ct-secondary bg-clip-text text-transparent">
          Конференции
        </span>
      </h1>

      <div className="mb-6 flex gap-2">
        <button
          onClick={() => switchSource('file')}
          className={`rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200 active:scale-[0.98] ${
            source === 'file'
              ? 'bg-gradient-to-r from-ct-primary to-ct-secondary text-ct-on-primary shadow-ct-lg'
              : 'border border-ct-border bg-ct-surface text-ct-text-muted shadow-ct-sm hover:-translate-y-0.5 hover:shadow-ct-md hover:text-ct-primary'
          }`}
        >
          Из файла Excel
        </button>
        <button
          onClick={() => switchSource('api')}
          className={`rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200 active:scale-[0.98] ${
            source === 'api'
              ? 'bg-gradient-to-r from-ct-primary to-ct-secondary text-ct-on-primary shadow-ct-lg'
              : 'border border-ct-border bg-ct-surface text-ct-text-muted shadow-ct-sm hover:-translate-y-0.5 hover:shadow-ct-md hover:text-ct-primary'
          }`}
        >
          Из внешнего API
        </button>
      </div>

      {source === 'api' && rejectedLoaded && rejectedList.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => setShowRejected(v => !v)}
            className="text-sm font-semibold text-ct-primary hover:text-ct-primary/80 transition-colors"
          >
            Отклонённые записи ({rejectedList.length}) {showRejected ? '▲' : '▼'}
          </button>
          {showRejected && (
            <div className="mt-2 max-h-60 overflow-auto rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm divide-y divide-ct-border">
              {rejectedList.map((r) => (
                <div key={r.directus_id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                  <span className="text-ct-text">
                    {r.l_name} {r.f_name}{r.company ? ` (${r.company})` : ''}
                  </span>
                  <button
                    onClick={() => handleRestore(r.directus_id)}
                    className="shrink-0 text-sm font-semibold text-ct-primary hover:text-ct-primary/80 transition-colors"
                  >
                    Восстановить
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {result ? (
        <div className="rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-8 text-center">
          <div className="text-lg font-extrabold text-ct-text">
            Импорт завершён
          </div>
          <div className="mt-2 text-ct-text-muted">
            Добавлено: {result.inserted} | Пропущено: {result.skipped}
          </div>
          <button
            onClick={handleReset}
            className={`mt-4 ${btnPrimary}`}
          >
            Импортировать ещё
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="append"
                checked={mode === 'append'}
                onChange={() => setMode('append')}
                className="accent-ct-primary"
              />
              <span className="text-sm text-ct-text">Добавить к имеющимся</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="replace"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
                className="accent-ct-primary"
              />
              <span className="text-sm text-ct-text">Очистить и заполнить по-новой</span>
            </label>
          </div>

          {source === 'file' && (
            <div className="mb-4 rounded-xl border-2 border-dashed border-ct-border bg-ct-surface/50 p-8 text-center transition-all duration-200 hover:border-ct-primary hover:bg-indigo-50/30 hover:shadow-ct-sm">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-sm font-semibold text-ct-primary hover:text-ct-primary/80 transition-colors"
              >
                Выберите файл .xlsx
              </label>
            </div>
          )}

          {source === 'api' && (!preview || (preview.length === 0 && !loading)) && (
            <div className="mb-4 rounded-xl border-2 border-dashed border-ct-border bg-ct-surface/50 p-8 text-center transition-all duration-200 hover:border-ct-primary hover:bg-indigo-50/30 hover:shadow-ct-sm">
              <button
                onClick={handleFetchApi}
                disabled={loading}
                className={btnPrimary}
              >
                {loading ? 'Загрузка...' : 'Загрузить из внешнего API'}
              </button>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
          )}

          {loading && !preview && (
            <div className="text-center text-ct-text-muted">Обработка...</div>
          )}

          {preview && preview.length === 0 && !loading && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
              {source === 'api'
                ? 'В данный момент еще нет поступивших заявок на участие в Конференции (0 строк)'
                : 'Файл не содержит валидных строк для импорта'}
            </div>
          )}

          {preview && preview.length > 0 && (
            <div>
              <div className="mb-3 rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-4">
                <div className="text-lg font-extrabold text-ct-text">
                  Записей к импорту: {previewTotal}
                </div>
                <div className="mt-1 text-sm text-ct-text-muted">
                  Получено из источника: {totalReceived > 0 ? totalReceived : previewTotal}
                  {' | '}Новых: {newCount}
                  {existingInPreview > 0 && mode === 'append' && ` | Уже в БД: ${existingInPreview}`}
                  {spamInPreview > 0 && ` | Спам: ${spamInPreview}`}
                  {errorRowCount > 0 && ` | С ошибками: ${errorRowCount}`}
                  {rejectedCount > 0 && ` | Скрыто отклонённых: ${rejectedCount}`}
                </div>
              </div>

              {spamInPreview > 0 && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                  <button
                    onClick={() => setShowSpamDetails(v => !v)}
                    className="font-semibold hover:underline"
                  >
                    Обнаружено возможных спам-записей: {spamInPreview} {showSpamDetails ? '▲' : '▼'}
                  </button>
                  {showSpamDetails && (
                    <div className="mt-2 max-h-60 overflow-auto divide-y divide-amber-200/60 rounded-lg bg-white/60">
                      {spamList.map((s) => (
                        <div key={s.directus_id} className="px-3 py-1.5">
                          {s.l_name} {s.f_name}{s.company ? ` (${s.company})` : ''} — {s.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {errorRowCount > 0 && (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <button
                    onClick={() => setShowErrorDetails(v => !v)}
                    className="font-semibold hover:underline"
                  >
                    Строк с ошибками (не попадут в импорт): {errorRowCount} {showErrorDetails ? '▲' : '▼'}
                  </button>
                  {showErrorDetails && (
                    <div className="mt-2 max-h-60 overflow-auto divide-y divide-red-200/60 rounded-lg bg-white/60">
                      {importErrors.map((err, i) => (
                        <div key={i} className="px-3 py-1.5">
                          Строка {err.row} · {FIELD_LABELS[err.field] ?? err.field}: {err.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {rejectedCount > 0 && (
                <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                  Скрыто ранее отклонённых записей: {rejectedCount}
                </div>
              )}

              {existingInPreview > 0 && mode === 'append' && (
                <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  Уже в базе данных: {existingInPreview} — они не будут продублированы
                </div>
              )}

              <div className="max-h-96 overflow-auto rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0">
                    <tr className="bg-gradient-to-r from-ct-primary to-ct-secondary text-ct-on-primary">
                      <th className="px-4 py-2.5 font-semibold text-ct-on-primary/90">Статус</th>
                      {Object.keys(preview[0]).map((col) => (
                        <th key={col} className="px-4 py-2.5 font-semibold text-ct-on-primary/90">
                          {col}
                        </th>
                      ))}
                      <th className="px-4 py-2.5 font-semibold text-ct-on-primary/90 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, i) => {
                      const globalIndex = pageStart + i;
                      const rowId = row.directus_id ? String(row.directus_id) : '';
                      const isExisting = mode === 'append' && rowId !== '' && existingIds.has(rowId);
                      const spamMessage = rowId !== '' ? spamMap.get(rowId) : undefined;
                      const isSpam = spamMessage !== undefined;
                      return (
                        <tr key={globalIndex} title={spamMessage ?? undefined} className={`border-t border-ct-border hover:bg-indigo-50/40 transition-colors duration-150 ${isSpam ? 'bg-amber-50/50' : isExisting ? 'bg-slate-50' : ''}`}>
                          <td className="px-4 py-2">
                            {isExisting ? (
                              <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                                В БД
                              </span>
                            ) : (
                              <span className="inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                                Новая
                              </span>
                            )}
                          </td>
                          {Object.values(row).map((val, j) => (
                            <td key={j} className={`px-4 py-2 ${isExisting && !isSpam ? 'text-ct-text-muted' : 'text-ct-text'}`}>
                              {String(val ?? '')}
                            </td>
                          ))}
                          <td className="px-4 py-2">
                            <button
                              onClick={() => removeRow(globalIndex)}
                              className="text-red-400 hover:text-red-600 text-lg leading-none transition-colors"
                              title="Удалить строку"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {previewTotalPages > 1 && (
                <div className="mt-2 flex items-center justify-between text-sm text-ct-text-muted">
                  <button
                    onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                    disabled={safePreviewPage <= 1}
                    className={btnSecondary}
                  >
                    Назад
                  </button>
                  <span>
                    Стр. {safePreviewPage} из {previewTotalPages} · показаны {pageStart + 1}–{Math.min(pageStart + PREVIEW_PAGE_SIZE, previewTotal)} из {previewTotal}
                  </span>
                  <button
                    onClick={() => setPreviewPage(p => Math.min(previewTotalPages, p + 1))}
                    disabled={safePreviewPage >= previewTotalPages}
                    className={btnSecondary}
                  >
                    Вперёд
                  </button>
                </div>
              )}

              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={loading}
                  className={btnPrimary}
                >
                  {loading ? 'Импорт...' : `Подтвердить импорт: ${newCount} (${mode === 'append' ? 'добавить' : 'заменить'})`}
                </button>
                <button
                  onClick={handleReset}
                  className={btnSecondary}
                >
                  Отмена
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <DeliveryPanel />
    </div>
  );
}
